import { FormlyFieldConfig } from '@ngx-formly/core';
import { IApplicationFormData } from '../../ui/models/application-form-data.model';
import { IServiceTranslation } from '../../ui/models/service-translation.model';
import { TranslateService } from '@ngx-translate/core';

// Define different function signatures for the build preview data functions
export type BuildPreviewDataFn = {
  // Signature for applicationFormBuildPreviewData
  (
    layout: FormlyFieldConfig[],
    layoutData: Record<string, unknown>,
    addLabelCount?: boolean
  ): IApplicationFormData[];

  // Signature for applicationFormBuildPreviewDataTranslated
  (
    layout: FormlyFieldConfig[],
    layoutData: Record<string, unknown>,
    serviceTranslations?: IServiceTranslation,
    addLabelCount?: boolean,
    translateService?: TranslateService
  ): IApplicationFormData[];
};

/**
 * Cleans service data by removing __formLayout property
 */
export const cleanServiceData = (
  serviceData: Record<string, unknown>
): Record<string, unknown> => {
  const cleanedData = { ...serviceData };
  delete cleanedData['__formLayout'];
  return cleanedData;
};

// Maps a value (or, for multi-select, each value in an array) to its option label.
export const resolveOptionSummaryLabels = (
  value: unknown,
  options: Record<string, unknown>[] | undefined,
  bindValue: string,
  bindLabel: string
): unknown => {
  if (!options || !Array.isArray(options)) {
    return value;
  }

  const lookupLabel = (raw: unknown): unknown => {
    const matchedOption = options.find(opt => opt[bindValue] === raw);
    return matchedOption?.[bindLabel] ?? raw;
  };

  if (Array.isArray(value)) {
    return value.map(lookupLabel);
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return lookupLabel(value);
  }

  return value;
};

// Resolves a summary field's value to its label(s) when showLabelInSummary is set.
export const resolveShowLabelInSummaryValue = (
  field: FormlyFieldConfig,
  value: unknown
): unknown => {
  if (field.props?.['showLabelInSummary'] !== true) {
    return value;
  }

  const bindLabel = field.props?.['bindLabel'] || 'label';

  if (
    field.props?.['getSummaryLabelFromValue'] === true &&
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  ) {
    const label = (value as Record<string, unknown>)[bindLabel];
    if (label) {
      return label;
    }
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    Array.isArray(value)
  ) {
    const options = field.props?.['options'] as Record<string, unknown>[];
    const bindValue = field.props?.['bindValue'] || 'value';
    return resolveOptionSummaryLabels(value, options, bindValue, bindLabel);
  }

  return value;
};
